/*
  Header watchlist count bubble + cross-device sync.

  Loaded on every page via the header link, which also carries the customer id
  and worker endpoint. That placement is deliberate: a heart clicked on a
  product page has to sync too, not just edits made on the watchlist page.

  SIGNED OUT
  Nothing leaves the browser. localStorage is the whole story, and the UI says
  so.

  SIGNED IN
  The local list and the stored list are merged by union, keyed on product id,
  keeping the earliest save time. Union rather than last-write-wins because
  either side may hold saves the other has never seen — a phone that has been
  offline for a week should not silently wipe the desktop list. The cost is
  that a removal on one device can be resurrected by a stale device; losing a
  removal is a smaller harm than losing a save.

  PRIVACY
  Only product ids and handles are sent. The customer id is asserted by the
  storefront and cannot be proven — Liquid has no way to sign it — so nothing
  that would matter if read by the wrong person is stored against it.
*/
(function () {
  var WATCH_KEY = 'savendo:watchlist';
  var syncing = false;

  function read() {
    try {
      var raw = window.localStorage.getItem(WATCH_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function write(list, silent) {
    try {
      window.localStorage.setItem(WATCH_KEY, JSON.stringify(list));
      if (!silent) {
        document.dispatchEvent(new CustomEvent('savendo:watchlist:change', { detail: { count: list.length } }));
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  function paint() {
    var count = read().length;
    document.querySelectorAll('[data-sv-wl-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count < 1;
    });
  }

  function context() {
    var link = document.querySelector('[data-sv-wl-link]');
    if (!link) return null;
    var customer = link.dataset.svCustomer;
    var endpoint = link.dataset.svEndpoint;
    if (!customer || !endpoint) return null; // signed out, or not configured
    return { customer: customer, endpoint: endpoint.replace(/\/$/, '') };
  }

  /** Union by product id; earliest save time wins. */
  function merge(a, b) {
    var byId = {};
    a.concat(b).forEach(function (item) {
      if (!item || !item.id) return;
      var key = String(item.id);
      if (!byId[key] || (item.at || 0) < (byId[key].at || 0)) {
        byId[key] = { id: key, handle: item.handle, at: item.at || Date.now() };
      }
    });
    return Object.keys(byId)
      .map(function (key) {
        return byId[key];
      })
      .sort(function (x, y) {
        return (y.at || 0) - (x.at || 0);
      });
  }

  function push(ctx, items) {
    return fetch(ctx.endpoint + '/list', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ customer: ctx.customer, kind: 'watchlist', items: items }),
      keepalive: true,
    }).catch(function () {
      return null;
    });
  }

  function sync() {
    var ctx = context();
    if (!ctx || syncing) return Promise.resolve();
    syncing = true;

    return fetch(ctx.endpoint + '/list?kind=watchlist&customer=' + encodeURIComponent(ctx.customer), {
      headers: { accept: 'application/json' },
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var remote = data && Array.isArray(data.items) ? data.items : [];
        var local = read();
        var merged = merge(local, remote);

        // Silent write: a normal write fires the change event, which would
        // call push() and re-enter this flow.
        if (merged.length !== local.length) write(merged, true);

        paint();
        document.dispatchEvent(new CustomEvent('savendo:watchlist:synced', { detail: { items: merged } }));

        if (merged.length !== remote.length) return push(ctx, merged);
        return null;
      })
      .catch(function () {
        return null;
      })
      .then(function () {
        syncing = false;
      });
  }

  function onLocalChange() {
    paint();
    var ctx = context();
    if (ctx) push(ctx, read());
  }

  function init() {
    paint();
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Fired by the heart on product pages and by removals on the watchlist page.
  document.addEventListener('savendo:watchlist:change', onLocalChange);
  // Back/forward cache restores a stale DOM — recount on restore.
  window.addEventListener('pageshow', paint);
})();
